import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { getApiUrl } from '@/lib/apiConfig';
import { API_BASE, PHOTOS_UPLOAD_BASE, PROD_UPLOADS_BASE, getApiDomain } from '@/lib/serverConfig';

// User data from PHP API (tbl_user)
export interface MemberUser {
  id: number | string;
  username: string;
  memberType: number;
  memberTypeName: string;
  email?: string;
  phone?: string;
  token: string;
  adminSession?: boolean; // Admin impersonation flag
  // Profile data based on member type
  profileData?: MemberProfile;
}

// Profile data from type-specific tables
export interface MemberProfile {
  id: number | string;
  status?: string;
  // Seconds since epoch, from MySQL's own UNIX_TIMESTAMP(updated_at) — see
  // the comment on this field in getMemberDashboard() (api.php) for why the
  // plain `updated_at` string isn't used instead (PHP's own default
  // timezone has been observed inconsistent across requests). Used by
  // ListingPage.tsx to tell a genuinely-stale localStorage draft apart from
  // one that's actually newer than the last real save.
  updated_at_epoch?: number;
  name?: string;
  address?: string;
  city?: string;
  cityId?: number;
  city_id?: number;
  state?: string;
  stateId?: number;
  state_id?: number;
  area?: string;
  areaId?: number;
  area_id?: number;
  pincode?: string;
  phone?: string;
  altPhone?: string;
  email?: string;
  website?: string;
  whatsapp?: string;
  description?: string;
  tagline?: string;
  services?: string[];
  awards?: string[];
  insurances?: string[];
  certifications?: string[];
  youtubeVideos?: { id: string; title: string }[];
  timing?: Record<string, { open: string; close: string; isOpen: boolean }>;
  logo?: string;
  image?: string;
  banner?: string;
  coverImage?: string;
  images?: string[];
  photoCount?: number;
  isVerified?: boolean;
  isPremium?: boolean;
  rating?: number;
  reviewCount?: number;
  views?: number;
  openingTime?: string;
  closingTime?: string;
  closedOn?: string;
  businessHours?: string;
  youtubeVideo?: string;
  // Doctor specific
  specialization?: string;
  specializationId?: number;
  qualification?: string;
  experience?: number | string;
  consultationFee?: number | string;
  clinicName?: string;
  hospitalAffiliations?: string;
  memberships?: string;
  gender?: string;
  languages?: string;
  // Medical Store / Diagnostic / Wholesaler
  drugLicense?: string;
  registrationNo?: string;
  gstNo?: string;
  shopName?: string;
  tinNumber?: string;
  homeDelivery?: string;
  // Hospital / Diagnostic specific
  noOfBeds?: number | string;
  noOfIcus?: number | string;
  specialities?: string;
  facilities?: string;
  insuranceCompanies?: string;
  tpaPartners?: string;
  governmentSchemes?: string;
  nablAccredited?: boolean;
  nablNumber?: string;
  testsOffered?: string;
  homeCollection?: boolean;
  booking_enabled?: boolean;
  booking_auto_disabled?: boolean;
  is_paid_plan?: boolean;
  ai_extracted_profile_json?: string;
  // Advanced listing fields
  social_facebook?: string;
  social_instagram?: string;
  social_twitter?: string;
  social_linkedin?: string;
  social_youtube?: string;
  youtube_videos?: string;
  instagram_posts?: string;
  facebook_posts?: string;
  achievements?: string | any[];
  before_after_cases?: string | any[];
  testimonials?: string | any[];
  insurance_partners?: string | any[];
  google_place_id?: string;
  google_rating?: number;
  google_review_count?: number;
  established_year?: string | number;
  products?: string;
  brands?: string;
  brandsDealt?: string;
  minimumOrder?: string;
  deliveryAreas?: string;
  productCategories?: string;
  // GPS coordinates
  latitude?: string | number;
  longitude?: string | number;
  offers_ads?: string;
}

interface MemberAuthContextType {
  user: MemberUser | null;
  loading: boolean;
  isLoggedIn: boolean;
  login: (username: string, password: string) => Promise<{ success: boolean; message: string; accountInactive?: boolean; selfDeactivated?: boolean }>;
  reactivateAccount: (username: string, password: string) => Promise<{ success: boolean; message: string }>;
  logout: () => void;
  updateProfile: (data: Partial<MemberProfile>) => Promise<{ success: boolean; message: string }>;
  updateAccountInfo: (data: { username?: string; email?: string; phone?: string }) => Promise<{ success: boolean; message: string }>;
  refreshProfile: () => Promise<void>;
}

const MemberAuthContext = createContext<MemberAuthContextType | undefined>(undefined);

// Use dynamic API URL from apiConfig
const getApiBaseUrl = () => getApiUrl();

// Decode exp from HMAC-SHA256 token (format: base64url_payload.base64url_sig)
function getTokenExp(token: string): number | null {
  try {
    const dot = token.indexOf('.');
    if (dot < 1) return null;
    const b64 = token.slice(0, dot).replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(atob(b64));
    return typeof payload.exp === 'number' ? payload.exp : null;
  } catch {
    return null;
  }
}

// Helper to resolve profile photo URL from various formats
function resolveProfilePhotoUrl(photo: string | undefined | null): string {
  if (!photo || photo.trim() === '') return '';
  
  // Already a full URL
  if (photo.startsWith('http://') || photo.startsWith('https://')) {
    return photo;
  }
  
  // Relative path from uploads - construct full URL
  if (photo.startsWith('/')) {
    const proto = typeof window !== 'undefined' ? window.location.protocol : 'https:';
    return `${proto}//${getApiDomain()}${photo}`;
  }
  
  if (photo.includes('uploads/photos/')) {
    return `${API_BASE}/${photo}`;
  }
  
  if (photo.includes('thumb_') || photo.includes('uploaded_files/')) {
    return `${PROD_UPLOADS_BASE.replace('/uploaded_files', '')}/${photo}`;
  }
  
  return `${PHOTOS_UPLOAD_BASE}/${photo}`;
}

export const MemberAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<MemberUser | null>(null);
  const [loading, setLoading] = useState(true);

  // Load user from localStorage on mount
  useEffect(() => {
    const run = async () => {
      const storedUser = localStorage.getItem('user');
      const storedToken = localStorage.getItem('token');

      if (storedUser && storedToken) {
        try {
          const parsedUser = JSON.parse(storedUser) as MemberUser;
          // Ensure memberType is a number for CATEGORY_LABELS lookup
          parsedUser.memberType = parseInt(String(parsedUser.memberType)) || 0;
          parsedUser.id = String(parsedUser.id).includes('-') ? String(parsedUser.id) : (parseInt(String(parsedUser.id)) || 0);
          if (!parsedUser.token && storedToken) {
            parsedUser.token = storedToken;
          }

          // Reject expired tokens client-side before trusting stored session
          const exp = getTokenExp(parsedUser.token);
          if (exp !== null && Math.floor(Date.now() / 1000) > exp) {
            localStorage.removeItem('user');
            localStorage.removeItem('token');
            setLoading(false);
            return;
          }

          setUser(parsedUser);
          // Was fire-and-forget (never awaited) — `loading` flipped false the
          // instant the basic localStorage user was parsed, well before this
          // background fetch actually populated user.profileData. Any page
          // that gates on profileData (My Listing's own auth check, the
          // dashboard header's displayName) rendered its "not logged in" /
          // generic "User" fallback during that gap on every single refresh,
          // even though the member really was logged in (confirmed live
          // 2026-08-20). Awaiting it keeps `loading` true for that whole
          // window instead.
          await refreshProfileData(parsedUser);
        } catch (error) {
          console.error('Error parsing stored user:', error);
          localStorage.removeItem('user');
          localStorage.removeItem('token');
        }
      }
      setLoading(false);
    };
    run();
  }, []);

  const refreshProfileData = async (currentUser: MemberUser) => {
    if (!currentUser?.token) return;
    
    try {
      // Use member_dashboard endpoint which returns complete profile data
      const memberId = currentUser.profileData?.id || '';

      // Kicked off in parallel with member_dashboard below — this fetch only
      // ever needed currentUser.id/memberType (both already known here), never
      // anything from member_dashboard's response, so nesting it AFTER that
      // fetch resolved was purely sequential dead time for no reason. Loading
      // now blocks the whole dashboard behind this (see the mount effect
      // above awaiting refreshProfileData) — confirmed live 2026-08-20 this
      // added ~300ms to every single dashboard load, exactly the two fetches'
      // response times added together instead of overlapped.
      const photosPromise = fetch(
        `${getApiBaseUrl()}?action=photos&user_id=${currentUser.id}&member_type=${currentUser.memberType}`,
        { method: 'GET' }
      )
        .then(async (photosRes) => {
          if (!photosRes.ok) return '';
          const photosJson = await photosRes.json();
          const photosArr = photosJson.data || photosJson.photos || [];
          if (Array.isArray(photosArr) && photosArr.length > 0) {
            const primary = photosArr.find((x: any) => x.is_primary) || photosArr[0];
            const raw = primary?.filepath || primary?.filename || primary?.url || primary?.thumb_url || primary?.photo_url || '';
            return resolveProfilePhotoUrl(raw);
          }
          return '';
        })
        .catch((e) => { console.warn('Could not fetch photos for primary avatar:', e); return ''; });

      const response = await fetch(
        `${getApiBaseUrl()}?action=member_dashboard&user_id=${currentUser.id}${memberId ? `&member_id=${memberId}` : ''}&member_type=${currentUser.memberType}&token=${encodeURIComponent(currentUser.token)}`,

        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        if (response.status === 401) {
          // Stale/invalid token that passed the client-side expiry check
          // (getTokenExp() above, mount-time) but was rejected server-side
          // (e.g. issued before a secret/DB reset) — same failure mode
          // updateProfile()/updateAccountInfo() already handle with an
          // identical check further down this file. Without this, a PWA
          // reopened after the server-side session went stale got stuck
          // showing whatever placeholder/blank profileData was last cached
          // by ProtectedMemberRoute's own localStorage-presence check (which
          // never round-trips to the server), instead of being sent back to
          // /auth (confirmed live 2026-09-05 on a real mobile PWA install).
          logout();
        }
        return;
      }

      {
        const data = await response.json();
        if (data.success && data.dashboard?.profile) {
          const p = data.dashboard.profile;
          // Sync memberType from server (in case DB was updated)
          const serverMemberType = parseInt(String(data.dashboard.member_type || currentUser.memberType)) || currentUser.memberType;

          // Decode HTML entities in text fields (prevents &amp;amp; double-encoding)
          const decodeHtmlEntities = (s: string): string =>
            s
              .replace(/&amp;/g, '&')
              .replace(/&quot;/g, '"')
              .replace(/&#34;/g, '"')
              .replace(/&#039;/g, "'")
              .replace(/&apos;/g, "'")
              .replace(/&lt;/g, '<')
              .replace(/&gt;/g, '>');

          // Helper to parse JSON strings to arrays
          const parseJsonArray = (value: any): string[] => {
            if (!value) return [];
            if (Array.isArray(value)) return value;

            if (typeof value === 'string') {
              const cleaned = decodeHtmlEntities(value).trim();
              try {
                const parsed = JSON.parse(cleaned);
                return Array.isArray(parsed) ? parsed : [];
              } catch {
                // If not valid JSON, split by comma
                return cleaned
                  .split(',')
                  .map((s: string) => s.trim())
                  .filter(Boolean);
              }
            }
            return [];
          };
          // Fetch primary photo from unified photos table (member_dashboard doesn't
          // always include it) — already in flight in parallel above, this just
          // waits for whichever of the two fetches was slower (usually already
          // resolved by the time we get here, so this is normally instant).
          const primaryPhotoUrl = await photosPromise;

          // Normalize backend keys to the frontend-friendly shape
          const normalizedProfile: MemberProfile = {
            id: String(p.id).includes('-') ? String(p.id) : (parseInt(p.id) || 0),
            // Raw providers.status ('pending' until admin-approved, matches
            // handleRegister() 2026-08-10) — surfaces the "pending approval"
            // banner on the dashboard even though login/dashboard access
            // itself was never gated on it.
            status: p.status || '',
            updated_at_epoch: typeof p.updated_at_epoch === 'number' ? p.updated_at_epoch : undefined,
            name: decodeHtmlEntities((() => {
              const mt = serverMemberType;
              if (mt === 2) return p.name || p.business_name || ''; // Doctor
              if (mt === 3) return p.pharmacy_name || p.shop_name || p.business_name || p.name || ''; // Medical Store
              if (mt === 4) return p.name || p.business_name || ''; // Nurse
              if (mt === 6) return p.name || p.business_name || ''; // Diagnostic
              if (mt === 7) return p.business_name || p.shop_name || p.name || ''; // Wholesaler
              if (mt === 8) return p.name || p.business_name || ''; // Blood Bank
              if (mt === 9) return p.name || p.business_name || ''; // Health Camp
              if (mt === 10) return p.name || p.business_name || ''; // Ambulance
              return p.name || p.hospital_name || p.shop_name || p.business_name || p.clinic_name || p.pharmacy_name || p.store_name || ''; // Hospital + fallback
            })()),
            tagline: decodeHtmlEntities(p.tagline || ''),
            description: decodeHtmlEntities(p.description || ''),
            address: p.address || '',
            city: p.city || p.city_name || '',
            cityId: parseInt(p.cityId || p.city_id) || undefined,
            city_id: parseInt(p.city_id || p.cityId) || undefined,
            state: p.state || p.state_name || '',
            stateId: parseInt(p.stateId || p.state_id) || undefined,
            state_id: parseInt(p.state_id || p.stateId) || undefined,
            area: p.area || p.area_name || '',
            areaId: parseInt(p.areaId || p.area_id) || undefined,
            area_id: parseInt(p.area_id || p.areaId) || undefined,
            pincode: p.pincode || '',
            phone: p.phone || '',
            altPhone: p.altPhone || p.alt_phone || '',
            email: p.email || '',
            website: p.website || '',
            whatsapp: p.whatsapp || '',
            // Resolve logo/image URL - prefer unified primary photo if available
            logo: primaryPhotoUrl || resolveProfilePhotoUrl(p.image || p.logo || p.primary_photo || p.photo_url || ''),
            image: primaryPhotoUrl || resolveProfilePhotoUrl(p.image || p.logo || p.primary_photo || p.photo_url || ''),
            banner: p.banner || '',
            isVerified: Boolean(p.isVerified || p.is_verified || p.is_verified === '1'),
            isPremium: Boolean(p.isPremium || p.is_premium || data.dashboard.is_premium),
            rating: parseFloat(p.rating) || 0,
            reviewCount: parseInt(p.reviewCount || p.review_count) || 0,
            views: parseInt(p.views || p.views_count) || 0,
            openingTime: p.openingTime || p.opening_time || '',
            closingTime: p.closingTime || p.closing_time || '',
            closedOn: p.closedOn || p.closed_on || '',
            businessHours: p.businessHours || p.business_hours || '',
            youtubeVideo: p.youtubeVideo || p.youtube_video || '',
            // Real uploaded-gallery count (getRealPhotoCount() server-side, the
            // actual `photos` table) — NOT images.length, which nothing ever
            // populates; Profile Completion's "Photos (min 5)" check used to be
            // permanently false for every real account because of that.
            photoCount: parseInt(p.photoCount) || 0,
            // Parse array fields
            services: parseJsonArray(p.services),
            awards: parseJsonArray(p.awards),
            certifications: parseJsonArray(p.certifications),
            insurances: parseJsonArray(p.insuranceCompanies || p.insurance_companies),
            // Doctor specific
            specialization: p.specialization || '',
            specializationId: parseInt(p.specializationId || p.specialization_id) || undefined,
            qualification: p.qualification || '',
            experience: p.experience || '',
            consultationFee: p.consultationFee || p.consultation_fee || '',
            clinicName: p.clinicName || p.clinic_name || '',
            hospitalAffiliations: p.hospitalAffiliations || p.hospital_affiliations || '',
            memberships: p.memberships || '',
            gender: p.gender || '',
            languages: p.languages || '',
            // License fields - normalized
            drugLicense: p.drugLicense || p.drug_license || p.license || p.dl_no || '',
            registrationNo: p.registrationNo || p.registration_no || '',
            gstNo: p.gstNo || p.gst_number || p.gstNumber || '',
            shopName: p.shopName || p.shop_name || '',
            tinNumber: p.tinNumber || p.tin_number || p.tin_no || '',
            homeDelivery: (typeof p.homeDelivery === 'string' ? p.homeDelivery : '') || p.home_delivery || '',
            // Hospital/Diagnostic
            noOfBeds: p.noOfBeds || p.no_of_beds || '',
            noOfIcus: p.noOfIcus || p.no_of_icus || '',
            specialities: p.specialities || '',
            facilities: p.facilities || '',
            insuranceCompanies: p.insuranceCompanies || p.insurance_companies || '',
            tpaPartners: p.tpaPartners || p.tpa_partners || '',
            governmentSchemes: p.governmentSchemes || p.government_schemes || '',
            nablAccredited: Boolean(p.nablAccredited || p.nabl_accredited || p.nabl_accredited === '1'),
            nablNumber: p.nablNumber || p.nabl_number || '',
            testsOffered: p.testsOffered || p.tests_offered || '',
            homeCollection: Boolean(p.homeCollection || p.home_collection || p.home_collection === '1'),
            booking_enabled: p.booking_enabled === true || p.booking_enabled === '1' || p.booking_enabled === 1,
            booking_auto_disabled: p.booking_auto_disabled === true || p.booking_auto_disabled === '1' || p.booking_auto_disabled === 1,
            is_paid_plan: Boolean(p.is_paid_plan),
            ai_extracted_profile_json: p.ai_extracted_profile_json || '',
            // Advanced listing fields — pass through from server
            social_facebook:     p.social_facebook || '',
            social_instagram:    p.social_instagram || '',
            social_twitter:      p.social_twitter || '',
            social_linkedin:     p.social_linkedin || '',
            social_youtube:      p.social_youtube || '',
            youtube_videos:      p.youtube_videos || '',
            instagram_posts:     p.instagram_posts || '',
            facebook_posts:      p.facebook_posts || '',
            achievements:        p.achievements || '',
            before_after_cases:  p.before_after_cases || '',
            testimonials:        p.testimonials || '',
            insurance_partners:  p.insurance_partners || '',
            google_place_id:     p.google_place_id || '',
            google_rating:       p.google_rating ?? undefined,
            google_review_count: p.google_review_count ?? undefined,
            offers_ads:          p.offers_ads || '',
            established_year:    p.established_year || p.establishedYear || '',
            products:            p.products || '',
            brands:              p.brands || '',
            brandsDealt:         p.brandsDealt || p.brands_dealt || '',
            minimumOrder:        p.minimumOrder || p.minimum_order || '',
            deliveryAreas:       p.deliveryAreas || p.delivery_areas || '',
            // Wholesaler-only. Was missing from this allowlist entirely — the
            // backend always saved and returned it correctly (member_dashboard's
            // raw `SELECT p.*`), but since this object is an explicit allowlist,
            // not a spread of the API response, any field left out here never
            // reaches user.profileData at all. ListingPage.tsx's form-init then
            // always saw undefined and fell back to "", so the field looked
            // blank on every reload no matter what was actually saved —
            // confirmed live 2026-09-12 (MediBulk Wholesalers, id=13).
            productCategories:   p.productCategories || p.product_categories || '',
            latitude:            p.latitude  ?? '',
            longitude:           p.longitude ?? '',
          };
          const updatedUser = {
            ...currentUser,
            memberType: serverMemberType,
            profileData: normalizedProfile,
          };
          setUser(updatedUser);
          localStorage.setItem('user', JSON.stringify(updatedUser));
        }
      }
    } catch (error) {
      console.error('Error refreshing profile:', error);
    }
  };

  // Shared by login() and reactivateAccount() — both end with the backend
  // returning the same {user, profile} shape and both need the identical
  // "actually log this browser in" side effects (state, storage, PWA
  // install linking, profile fetch).
  const applyLoggedInUser = async (data: any) => {
    const rawType = data.user.memberType ?? data.user.member_type;
    const rawTypeName = data.user.memberTypeName ?? data.user.member_type_name;
    const rawId = data.user.id ?? data.user.user_id;
    const userData: MemberUser = {
      id: String(rawId).includes('-') ? String(rawId) : (parseInt(String(rawId)) || 0),
      username: data.user.username || '',
      memberType: parseInt(String(rawType)) || 0,
      memberTypeName: rawTypeName || '',
      email: data.user.email || '',
      phone: data.user.phone || '',
      token: data.user.token || '',
      profileData: data.user.profileData || data.user.profile_data,
    };

    setUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.setItem('token', userData.token);

    const numericId = typeof userData.id === 'string' ? parseInt(userData.id) : userData.id as number;
    if (numericId > 0) {
      import('@/utils/installTracker').then(({ linkInstallToUser, isAppInstalled }) => {
        if (isAppInstalled()) linkInstallToUser(numericId).catch(() => {});
      });
    }

    await refreshProfileData(userData);
  };

  const login = async (username: string, password: string): Promise<{ success: boolean; message: string; accountInactive?: boolean; selfDeactivated?: boolean }> => {
    try {
      const { shouldIntercept, interceptAuth } = await import('@/lib/authBridge');
      let data: any;
      if (shouldIntercept('login')) {
        try {
          data = await interceptAuth('login', { username, password });
        } catch (err: any) {
          return { success: false, message: err?.message || 'Login failed' };
        }
      } else {
        const response = await fetch(`${getApiBaseUrl()}?action=login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ username, password }),
        });
        data = await response.json();
      }

      if (data.success && data.user) {
        await applyLoggedInUser(data);
        return { success: true, message: 'Login successful' };
      } else {
        return {
          success: false,
          message: data.message || 'Invalid credentials',
          accountInactive: !!data.account_inactive,
          selfDeactivated: !!data.self_deactivated,
        };
      }
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, message: 'Connection error. Please try again.' };
    }
  };

  // Undo of "Surrender Account" (SettingsPage.tsx) — only succeeds when the
  // account was deactivated by the member themselves, not by an admin (see
  // handleMemberReactivateOwnAccount(), api.php). Re-authenticates with the
  // same credentials since an inactive account holds no valid session.
  const reactivateAccount = async (username: string, password: string): Promise<{ success: boolean; message: string }> => {
    try {
      const response = await fetch(`${getApiBaseUrl()}?action=member_reactivate_own_account`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });
      const data = await response.json();

      if (data.success && data.user) {
        await applyLoggedInUser(data);
        return { success: true, message: data.message || 'Your account has been reactivated.' };
      }
      return { success: false, message: data.message || 'Failed to reactivate account.' };
    } catch (error) {
      console.error('Reactivate account error:', error);
      return { success: false, message: 'Connection error. Please try again.' };
    }
  };

  const logout = useCallback(() => {
    setUser(null);
    localStorage.removeItem('user');
    localStorage.removeItem('token');
  }, []);

  const updateProfile = async (data: Partial<MemberProfile>): Promise<{ success: boolean; message: string }> => {
    if (!user?.id) {
      return { success: false, message: 'Not authenticated' };
    }

    try {
      // Backend requires token + user_id and member_type.
      // member_id pins the update to this exact listing — without it, accounts
      // with more than one listing under the same category (multi-location)
      // silently edit the wrong one.
      const requestBody = {
        ...data,
        user_id: user.id,
        member_type: user.memberType,
        member_id: user.profileData?.id,
        token: user.token,
      };

      const response = await fetch(`${getApiBaseUrl()}?action=update_profile`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(user.token ? { 'Authorization': `Bearer ${user.token}` } : {}),
        },
        body: JSON.stringify(requestBody),
      });

      const result = await response.json();

      if (result.success) {
        // Update local user data
        const updatedUser = {
          ...user,
          profileData: {
            ...user.profileData,
            ...data,
          } as MemberProfile,
        };
        setUser(updatedUser);
        localStorage.setItem('user', JSON.stringify(updatedUser));

        return { success: true, message: 'Profile updated successfully' };
      } else if (response.status === 401 || /invalid.*token|expired.*token/i.test(result.message || '')) {
        // A stale/invalid token that passed the client-side expiry check but was
        // rejected server-side (e.g. issued before a secret/DB reset) leaves the
        // user stuck on a page that can never save — force a clean re-login
        // instead of surfacing the raw backend message.
        logout();
        return { success: false, message: 'Your session has expired. Please log in again.' };
      } else {
        return { success: false, message: result.message || 'Update failed' };
      }
    } catch (error) {
      console.error('Profile update error:', error);
      return { success: false, message: 'Connection error. Please try again.' };
    }
  };

  // Login-account fields (users.full_name/email/phone — the dashboard's
  // "Account Information" card) — deliberately separate from updateProfile()
  // above, which writes to the business listing (`providers`), not the login
  // row. Updates BOTH localStorage AND the top-level user.username/email/phone
  // (not just profileData) — updateProfile() only touching profileData was
  // exactly why an "Account Information" save used to look successful but
  // reappear with the old value on next reload: the stale top-level field
  // always overrides profileData wherever this page reads from.
  const updateAccountInfo = async (data: { username?: string; email?: string; phone?: string }): Promise<{ success: boolean; message: string }> => {
    if (!user?.id) {
      return { success: false, message: 'Not authenticated' };
    }

    try {
      const response = await fetch(`${getApiBaseUrl()}?action=update_account_info`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(user.token ? { 'Authorization': `Bearer ${user.token}` } : {}),
        },
        body: JSON.stringify({ ...data, token: user.token }),
      });

      const result = await response.json();

      if (result.success) {
        const updatedUser = {
          ...user,
          username: result.data?.username ?? user.username,
          email: result.data?.email ?? user.email,
          phone: result.data?.phone ?? user.phone,
        };
        setUser(updatedUser);
        localStorage.setItem('user', JSON.stringify(updatedUser));
        return { success: true, message: result.message || 'Account information updated' };
      } else if (response.status === 401 || /invalid.*token|expired.*token/i.test(result.message || '')) {
        logout();
        return { success: false, message: 'Your session has expired. Please log in again.' };
      } else {
        return { success: false, message: result.message || 'Update failed' };
      }
    } catch (error) {
      console.error('Account info update error:', error);
      return { success: false, message: 'Connection error. Please try again.' };
    }
  };

  const refreshProfile = useCallback(async () => {
    if (user) {
      await refreshProfileData(user);
    }
  }, [user]);

  const contextValue = useMemo(() => ({
    user,
    loading,
    isLoggedIn: !!user,
    login,
    reactivateAccount,
    logout,
    updateProfile,
    updateAccountInfo,
    refreshProfile,
  }), [user, loading, login, reactivateAccount, logout, updateProfile, updateAccountInfo, refreshProfile]);

  return (
    <MemberAuthContext.Provider value={contextValue}>
      {children}
    </MemberAuthContext.Provider>
  );
};

export const useMemberAuth = () => {
  const context = useContext(MemberAuthContext);
  if (context === undefined) {
    throw new Error('useMemberAuth must be used within a MemberAuthProvider');
  }
  return context;
};
